import { StrictMode, Suspense, lazy, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { MotionConfig } from 'motion/react';
import './styles/app.css';
import { SiteSky } from './components/SiteSky.tsx';

// Each surface is its own chunk: phones never download the stage, host or studio code.
const StageApp = lazy(() => import('./surfaces/stage/Stage.tsx').then((m) => ({ default: m.StageApp })));
const HostApp = lazy(() => import('./surfaces/host/Host.tsx').then((m) => ({ default: m.HostApp })));
const PlayerApp = lazy(() => import('./surfaces/player/Player.tsx').then((m) => ({ default: m.PlayerApp })));
const AdminProApp = lazy(() => import('./surfaces/adminpro/AdminPro.tsx').then((m) => ({ default: m.AdminProApp })));
const AdminApp = lazy(() => import('./surfaces/admin/Admin.tsx').then((m) => ({ default: m.AdminApp })));
const TvApp = lazy(() => import('./surfaces/tv/Tv.tsx').then((m) => ({ default: m.TvApp })));
const LegalApp = lazy(() => import('./surfaces/legal/Legal.tsx').then((m) => ({ default: m.LegalApp })));
const ResetPasswordApp = lazy(() => import('./surfaces/auth/ResetPassword.tsx').then((m) => ({ default: m.ResetPasswordApp })));
const HomeApp = lazy(() => import('./surfaces/home/Home.tsx').then((m) => ({ default: m.HomeApp })));

/**
 * Light mode: smart TVs and TV sticks have weak graphics chips, so heavy decoration (glass blur,
 * a moving full-screen background, glows, endless loops) makes the game stutter. On those devices
 * the decoration is simplified; the game's own animations stay. ?lite=1 / ?lite=0 forces it (remembered).
 */
function chooseLiteMode() {
  try {
    const forced = new URLSearchParams(location.search).get('lite');
    if (forced === '1' || forced === '0') localStorage.setItem('majlis.lite', forced);
    const saved = localStorage.getItem('majlis.lite');
    const tv = /SmartTV|SMART-TV|Tizen|Web0S|WebOS|NetCast|BRAVIA|Android TV|GoogleTV|AFT[A-Z]|CrKey|HbbTV|VIDAA|Roku|PhilipsTV|Hisense|AmazonWebAppPlatform/i.test(navigator.userAgent);
    const weak = (navigator.hardwareConcurrency ?? 8) <= 4 || ((navigator as { deviceMemory?: number }).deviceMemory ?? 8) <= 2;
    const lite = saved ? saved === '1' : tv || (weak && location.pathname.startsWith('/stage'));
    document.documentElement.classList.toggle('perf-lite', lite);
  } catch {
    /* storage blocked: keep the full look */
  }
}
chooseLiteMode();

// A page left open across an update may ask for a file of the old build: reload once to get the new one.
addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  try {
    const last = Number(sessionStorage.getItem('majlis.reloadedAt') ?? 0);
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem('majlis.reloadedAt', String(Date.now()));
  } catch {
    /* storage blocked: reload anyway */
  }
  location.reload();
});

function usePath() {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const on = () => setPath(location.pathname);
    addEventListener('popstate', on);
    return () => removeEventListener('popstate', on);
  }, []);
  const navigate = (to: string) => {
    history.pushState(null, '', to);
    setPath(new URL(to, location.href).pathname);
  };
  return { path, navigate };
}

function App() {
  const { path, navigate } = usePath();
  const seg = path.split('/').filter(Boolean);
  document.body.dataset.surface = seg[0] === 'admin-pro' ? 'admin' : (seg[0] ?? 'home');
  switch (seg[0]) {
    case 'stage':
      return seg[1] ? <StageApp sessionId={seg[1]} /> : null;
    case 'host':
      return seg[1] ? <HostApp sessionId={seg[1]} /> : null;
    case 'play':
      return <PlayerApp roomCode={seg[1] ?? null} />;
    case 'tv':
      return <TvApp navigate={navigate} />;
    case 'admin-pro':
      return <AdminProApp />;
    case 'admin':
      return <AdminApp />;
    case 'reset':
      return <ResetPasswordApp navigate={navigate} />;
    case 'privacy':
    case 'terms':
      return <LegalApp doc={seg[0]} />;
    default:
      return <HomeApp navigate={navigate} />;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* "user": honour prefers-reduced-motion across every motion primitive. */}
    <MotionConfig reducedMotion="user">
      {/* The site's sky behind every game page (not the owner's studio). */}
      {/* No twinkling stars on the phones (owner, 2026-10-25: lighter for the phone). */}
      {!location.pathname.startsWith('/admin') && !location.pathname.startsWith('/play') && <SiteSky />}
      <Suspense fallback={<div className="boot" aria-busy="true" />}>
        <App />
      </Suspense>
    </MotionConfig>
  </StrictMode>,
);
